import React, { useEffect, useState } from 'react';
import { searchClassroomByName } from '../../helpers/searchClassRoomByName';
import { Button, Form, InputGroup, ListGroup, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExchangeAlt,
  faSearch,
  faTimes,
  faArrowRight,
  faInfoCircle,
  faUserGraduate,
} from '@fortawesome/free-solid-svg-icons';
import { notify } from 'react-notify-toast';
import PromoteModal from './PromoteModal';
import clientAxios from '../../clients/clientAxios';
import './commission.css';

const PromoteStudents = ({ commission }) => {
  const [searchValue, setSearchValue] = useState('');
  const [suggestionsVisible, setSuggestionsVisible] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [newClassroom, setNewClassroom] = useState('');
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedStudents, setProcessedStudents] = useState({
    success: [],
    failed: [],
  });

  const handleChangeSearchValue = (event) => {
    const inputValue = event?.target?.value;
    setSearchValue(inputValue);
    if (!inputValue) {
      setNewClassroom('');
    }
    if (inputValue && inputValue.length >= 2) {
      setSuggestionsVisible(true);
    } else {
      setSuggestionsVisible(false);
    }
  };

  const handleClearInput = () => {
    setSearchValue('');
    setNewClassroom('');
    setSuggestionsVisible(false);
  };

  useEffect(() => {
    if (searchValue?.length >= 2) {
      searchClassroomByName(setIsLoading, setSuggestions, searchValue, true);
    }
  }, [searchValue]);

  const handleNewCommission = (selectedClassroom) => {
    if (selectedClassroom?._id === commission?._id) {
      notify.show('No puedes promover a la misma comisión', 'error', 3000);
      return;
    }
    setSearchValue(selectedClassroom?.classroom);
    setNewClassroom(selectedClassroom?._id);
    setSuggestionsVisible(false);
  };

  const handlePromote = async () => {
    setIsProcessing(true);
    const user = JSON.parse(localStorage.getItem('userdata'));
    try {
      const { data } = await clientAxios.post(
        `/classroom/promote/${commission?._id}`,
        { newClassroomId: newClassroom, userId: user?.id }
      );

      setProcessedStudents({
        success: data?.success || [],
        failed: data?.failed || [],
      });
    } catch (error) {
      notify.show('Error al procesar la solicitud', 'error', 3000);
      setProcessedStudents({
        success: [],
        failed: ['Error al procesar la solicitud'],
      });
    } finally {
      setIsProcessing(false);
      handleClearInput();
    }
  };

  const handleCloseModal = () => {
    setShowPromoteModal(false);
    setProcessedStudents({
      success: [],
      failed: [],
    });
  };

  return (
    <div>
      <div className='d-flex align-items-center justify-content-between pb-3 border-bottom mb-4'>
        <div className='d-flex align-items-center'>
          <div
            className='mr-2 rounded-circle d-flex align-items-center justify-content-center'
            style={{
              width: 36,
              height: 36,
              backgroundColor: '#eff6ff',
              color: '#2563eb',
            }}
          >
            <FontAwesomeIcon icon={faExchangeAlt} />
          </div>
          <div>
            <h5 className='mb-0 font-weight-bold text-dark'>
              Promover Estudiantes
            </h5>
            <small className='text-muted'>
              Migrar alumnos activos de esta comisión hacia una nueva comisión
            </small>
          </div>
        </div>
      </div>

      <div className='commission-section-card'>
        <div className='commission-section-title'>
          <FontAwesomeIcon icon={faUserGraduate} className='text-primary' />
          Comisión de Destino
        </div>

        <Form.Group className='mb-0'>
          <Form.Label className='commission-form-label'>
            Seleccionar comisión destino *
          </Form.Label>
          <div className='search-bar-container'>
            <InputGroup>
              <InputGroup.Prepend>
                <InputGroup.Text className='bg-white border-right-0 text-muted'>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
              </InputGroup.Prepend>
              <Form.Control
                type='text'
                className='border-left-0 commission-input'
                placeholder='Buscar comisión por nombre...'
                value={searchValue}
                onChange={handleChangeSearchValue}
                disabled={!commission?._id}
              />
              {searchValue && (
                <InputGroup.Append>
                  <Button
                    variant='outline-secondary'
                    className='border-left-0'
                    onClick={handleClearInput}
                  >
                    <FontAwesomeIcon icon={faTimes} size='xs' />
                  </Button>
                </InputGroup.Append>
              )}
            </InputGroup>

            {suggestionsVisible && (
              <ListGroup className='suggestions-list'>
                {isLoading ? (
                  <ListGroup.Item className='text-center py-3'>
                    <Spinner
                      animation='border'
                      role='status'
                      size='sm'
                      variant='primary'
                    />
                    <span className='ml-2 text-muted small'>Buscando...</span>
                  </ListGroup.Item>
                ) : suggestions && suggestions.length > 0 ? (
                  suggestions.map((suggestion) => (
                    <ListGroup.Item
                      key={suggestion?._id}
                      action
                      onClick={() => handleNewCommission(suggestion)}
                    >
                      <div className='font-weight-bold text-dark'>
                        {suggestion?.classroom}
                      </div>
                    </ListGroup.Item>
                  ))
                ) : (
                  <ListGroup.Item className='text-muted small'>
                    No se encontraron coincidencias
                  </ListGroup.Item>
                )}
              </ListGroup>
            )}
          </div>
        </Form.Group>
      </div>

      <div className='d-flex align-items-center justify-content-between pt-3 border-top mt-3'>
        <div className='text-muted small'>
          {!commission?._id ? (
            <span>
              <FontAwesomeIcon icon={faInfoCircle} className='mr-1' />
              Selecciona primero una comisión de origen en la barra superior
            </span>
          ) : (
            <span>
              Se promoverán los estudiantes de{' '}
              <strong className='text-dark'>{commission.classroom}</strong>
            </span>
          )}
        </div>

        <Button
          variant='primary'
          type='button'
          className='px-4 py-2 font-weight-bold shadow-sm'
          disabled={!newClassroom || !commission?._id}
          onClick={() => setShowPromoteModal(true)}
        >
          <FontAwesomeIcon icon={faArrowRight} className='mr-2' />
          Promover alumnos
        </Button>
      </div>

      <PromoteModal
        show={showPromoteModal}
        onHide={handleCloseModal}
        OnPromote={handlePromote}
        isProcessing={isProcessing}
        processedStudents={processedStudents}
      />
    </div>
  );
};

export default PromoteStudents;